import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import {
  catchError,
  combineLatest,
  delay,
  EMPTY,
  filter,
  finalize,
  fromEvent,
  map,
  Observable,
  scan,
  shareReplay,
  take
} from 'rxjs'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { Store } from '@ngxs/store'
import { ErrorState } from '../store/error/error.state'
import { AuthState } from '../store/auth/auth.state'
import { PreferenceState } from '../store/preference/preference.state'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment/moment'
import { registerLocaleData } from '@angular/common'
import { UserState } from '../store/user/user.state'
import { UserInterface } from '../shared/model/user.model'
import {
  ActivationEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Event as NavigationEvent,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterEvent
} from '@angular/router'
import { NotificationState } from '../store/notification/notification.state'
import { NzModalService } from 'ng-zorro-antd/modal'
import { PccState } from '../store/pcc/pcc.state'
import { TestModeComponent } from './test-mode/test-mode.component'
import { environment } from '../environments/environment'
import { RootStore } from '../store/root-store'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { NetworkService } from '../shared/services/network.service'
import { PccWindowCommands } from '../shared/enums/pcc-window-commands.enum'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout'
import { ZoomVideoState } from '../store/zoom-video/zoom-video.state'
import { RouteName } from '../core/enums/route-name.enum'
import { IntegrationsService } from '../shared/services/integrations.service'
import { UserActivityService } from '../shared/services/user-activity/user-activity.service'
import { LabelsState } from '../store/labels/labels.state'
import MeasurementServicePlugin from '../shared/capacitor/measurement/measurements-plugin'
import { OfflineActivityService } from '../shared/services/offline-activity.service'
import { BatteryService } from '../shared/services/battery.service'
import { OrganizationState } from '../store/organization/organization.state'
import { CapacitorPluginsFacade } from '../shared/capacitor/capacitor-plugins-facade.service'
import { LogrocketPFFacade } from '../shared/services/logrocket-pf-facade.service'
import { LogrocketTrackKeys } from '../shared/enums/logrocket-track-keys'
import { FeatureParams } from '../shared/model/config'
import { ConfigService } from '../shared/services/config.service'
import { BffState } from '../store/bff/bff.state'
import { BootstrapConfigService } from '../shared/services/bootstrap-config.service'
import { OrganizationEmrType } from '../shared/model/organization.model'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly errorState = inject(ErrorState)
  private readonly notification = inject(NzNotificationService)
  private readonly translate = inject(TranslateService)
  private readonly notificationState = inject(NotificationState)
  private readonly modal = inject(NzModalService)
  private readonly userState = inject(UserState)
  private readonly networkService = inject(NetworkService)
  private readonly pccState = inject(PccState)
  public readonly authState = inject(AuthState)
  public readonly preferenceState = inject(PreferenceState)
  public readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly store = inject(Store)
  private readonly breakPointObserver = inject(BreakpointObserver)
  private readonly zoomVideoState = inject(ZoomVideoState)
  private readonly bffState = inject(BffState) // is needed to initialize event bus from bff
  private readonly integrationsService = inject(IntegrationsService)
  private readonly userActivityService = inject(UserActivityService)
  private readonly batteryService = inject(BatteryService)
  private readonly offlineActivityService = inject(OfflineActivityService)
  private readonly capacitorPluginsFacade = inject(CapacitorPluginsFacade)
  private readonly logrocketPFFacade = inject(LogrocketPFFacade)
  private readonly configService = inject(ConfigService)
  private readonly bootstrapConfigService = inject(BootstrapConfigService)

  errorMessage$: Observable<string | null> = this.store.select(
    ErrorState.errorMessage
  )
  isAuthorized$: Observable<boolean> = this.store.select(
    AuthState.isAuthenticated
  )
  userProfile$: Observable<any> = this.store.select(UserState.userProfile)
  language$ = this.store.select(PreferenceState.language)
  user$: Observable<UserInterface | null> = this.store.select(
    UserState.currentUser
  )
  isMfaRequired$: Observable<boolean> = this.store.select(
    PreferenceState.isMfaRequired
  )
  isSessionExpire$: Observable<boolean> = this.store.select(
    PreferenceState.sessionExpire
  )
  notification$ = this.store.select(PreferenceState.notification)
  screenSaver$: Observable<boolean> = this.store.select(
    PreferenceState.screenSaver
  )
  protected readonly screenSaverBuffered$ = this.screenSaver$.pipe(
    filter(value => !!value),
    scan(acc => acc + 1, 0),
    map(value => value % 4 === 0)
  )
  protected readonly isZoomCallPage$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(event => event.url.includes(RouteName.ZoomCall)),
    shareReplay({ bufferSize: 1, refCount: true })
  )
  pccLoginSettingsTabletErrorShown$ = this.store.select(
    AuthState.pccLoginSettingsTabletErrorShown
  )
  pccLoginWasNotPerformedButClosed$: Observable<boolean> = this.store.select(
    AuthState.pccLoginWasNotPerformedButClosed
  )
  pccLoginWasPerformedWithCNAUser$: Observable<boolean> = this.store.select(
    PreferenceState.pccLoginWasPerformedWithCNAUser
  )
  screenBlockedStatus$: Observable<string> = this.store.select(
    RootStore.screenBlockedStatus
  )
  isForcedResolution$: Observable<boolean | undefined> = this.store.select(
    PreferenceState.isForcedResolution
  )
  incorrectTimeSystem$: Observable<boolean> = this.store.select(
    PreferenceState.incorrectTimeSystem
  )
  userShouldWatchDisclaimer = this.store.selectSignal(
    UserState.isShouldWatchDisclaimer
  )
  hasVirtualKeyboard = this.store.selectSignal(
    PreferenceState.hasVirtualKeyboard
  )
  hiddenFromPccWindow = inject(Store).selectSignal(
    PreferenceState.hasHiddenFromPccWindow
  )
  userWithNoDegree = this.store.selectSignal(AuthState.isUserWithNoDegree)
  nonDegreeUserSelectedCNA = this.store.selectSignal(
    PreferenceState.nonDegreeUserSelectedCNA
  )
  protected readonly hasNotificationAccess = this.store.selectSignal(
    PreferenceState.hasNotificationAccess
  )
  protected hasCNAAccessToken = this.store.selectSignal(
    AuthState.hasCNAAccessToken
  )
  protected readonly isSession = this.store.selectSignal(
    ZoomVideoState.inSession
  )
  protected readonly isPhoneDevice = toSignal(
    this.breakPointObserver
      .observe([Breakpoints.Handset])
      .pipe(map(result => result.matches))
  )
  protected readonly lastClickedEMRName = this.store.selectSignal(
    AuthState.lastClickedEMRName
  )
  protected readonly patientLabels = this.store.selectSignal(
    LabelsState.patientLabels
  )
  protected readonly emrLoginSettingsTabletErrorInvitationTimeoutShown =
    this.store.selectSignal(
      AuthState.emrLoginSettingsTabletErrorInvitationTimeoutShown
    )
  protected readonly loginFailedError = this.store.selectSignal(
    AuthState.loginFailedError
  )
  protected readonly isOrganizationsLoadingFailedAndOffline =
    this.store.selectSignal(
      OrganizationState.isOrganizationsLoadingFailedAndOffline
    )
  protected readonly isUserLoadingFailedAndOffline = this.store.selectSignal(
    UserState.isUserLoadingFailedAndOffline
  )
  protected readonly isSystemAboutToRestartPopupShown = this.store.selectSignal(
    PreferenceState.isSystemAboutToRestartPopupShown
  )
  protected readonly isSelectNetworkPopupShown = this.store.selectSignal(
    PreferenceState.isSelectNetworkPopupShown
  )
  protected readonly isSimulationMode = this.store.selectSignal(
    PreferenceState.isSimulationMode
  )
  protected readonly backendServicesStatus = this.store.selectSignal(
    BffState.backendServicesStatus
  )
  protected readonly isPopupAboutWaitingWiFiToBeResumedShown =
    this.offlineActivityService.isPopupAboutWaitingWiFiToBeResumedShown
  protected readonly isBatteryRunningLowScreenShownAndAuthenticated =
    this.batteryService.isBatteryRunningLowScreenShownAndAuthenticated
  protected readonly isBatteryRunningLowScreenShownAndNotAuthenticated =
    this.batteryService.isBatteryRunningLowScreenShownAndNotAuthenticated
  protected readonly isBatteryNoPowerAndConnectScreenShown =
    this.batteryService.isBatteryNoPowerAndConnectScreenShown
  protected readonly isBatteryCriticallyLowScreenShown =
    this.batteryService.isBatteryCriticallyLowScreenShown
  protected readonly isBatteryTooLowToRunScreenShown =
    this.batteryService.isBatteryTooLowToRunScreenShown
  protected readonly isBatteryConnectToPowerAndShuttingDownScreenShown =
    this.batteryService.isBatteryConnectToPowerAndShuttingDownScreenShown
  protected readonly minutesTillShutDown =
    this.batteryService.minutesTillShutDown
  protected readonly deviceBatteryLevel = this.batteryService.deviceBatteryLevel
  protected readonly isDeviceCharging = this.batteryService.isDeviceCharging
  protected readonly hasChargerJustConnected =
    this.batteryService.hasChargerJustConnected
  protected readonly secondsBeforeRestart = this.configService.getNumberValue(
    FeatureParams.FeatureSecondsBeforeRestart,
    60
  )
  protected readonly storedDepartment = this.store.selectSignal(
    PreferenceState.preferenceDepartment
  )
  protected readonly organizationEmrType = this.store.selectSignal(
    PreferenceState.organizationEmrType
  )
  private readonly isAuthLoading = this.store.selectSignal(AuthState.isLoading)
  private readonly isPccReturning = this.store.selectSignal(
    AuthState.isPccReturning
  )
  private readonly pccLogouting = this.store.selectSignal(PccState.pccLogouting)

  protected readonly isLoaderShown = computed(() => {
    const isAuthLoading = this.isAuthLoading()
    const isPccReturning = this.isPccReturning()
    const pccLogouting = this.pccLogouting()
    return isAuthLoading || isPccReturning || pccLogouting
  })
  protected readonly storedDepartmentTransformed = computed(() => {
    const storedDepertment = this.storedDepartment()
    if (Array.isArray(storedDepertment)) {
      return storedDepertment[0]
    }
    return storedDepertment
  })

  public isUrlNotLoginOrResidentOrInsight = toSignal(
    this.router.events.pipe(
      filter(
        (event: NavigationEvent | RouterEvent) =>
          event instanceof NavigationEnd || event instanceof NavigationSkipped
      ),
      map(
        e =>
          e.url !== '/login' &&
          !e.url.includes(RouteName.Resident) &&
          !e.url.includes('/' + RouteName.Insight + '/')
      )
    ),
    { initialValue: false }
  )
  @ViewChild('testModeContainer', { read: ViewContainerRef })
  testModeContainer!: ViewContainerRef
  isShowClockUpdateModal = false

  protected pccLoginErrorTechnicalDetailsVisible: boolean = false
  protected readonly isMobile = this.preferenceState.isMobile
  protected readonly isBrowserSupported =
    this.preferenceState.isBrowserSupported
  protected readonly isOffline$ = this.networkService.isOfflineDebounced$
  protected readonly isOnline = toSignal(this.networkService.isOnlineObs$)
  protected readonly isInitialLoading = signal<boolean>(true)
  protected readonly isFirstNavigationPerformed = signal<boolean>(true)
  protected readonly OrganizationEmrType = OrganizationEmrType

  constructor() {
    const translate = this.translate
    translate.setDefaultLang('en')
    this.controlDeviceSleepReturning()
    this.monitorUserWindowTabFocusing()
    this.handleNativeDeviceVirtualKeyboardExistence()
    //Disable web side live update, will be controlled from Android
    this.handleFirstNavigationLoader()
    this.userActivityService.subscribeToSessionActivityTracking()
    this.monitorScreenSaverChanges()
    this.monitorAuthorizedStatus()
    this.batteryService.init()
    this.listenToDeviceAboutToRestart()
    this.notifyNativePartAboutBeingAlive()
  }

  @HostListener('window:resize')
  onResize() {
    this.setWindowZoom()
  }

  ngOnInit(): void {
    this.initializeListeners()
    this.pccBroadcastChannelListener()
  }

  ngAfterViewInit() {
    if (environment?.test) {
      this.addTestModeComponent()
    }
  }

  addTestModeComponent() {
    this.testModeContainer.clear()
    this.testModeContainer.createComponent(TestModeComponent)
  }

  initializeListeners(): void {
    this.setMfaListenerSetting()
    this.errorMessageListenerSetting()
    this.languageListenerSetting()
    // this.setNotificationPermissionSetting()
    this.setRouterConfigLogic()
    this.setWindowZoom()
    this.authState.checkTokenStatus()
    this.zoomVideoState.setInSessionFromLocalStorage()
  }

  setWindowZoom() {
    if (this.isMobile) return
    // @ts-ignore
    // document.body.style.zoom = window.innerWidth / 1920
  }

  errorMessageListenerSetting() {
    this.errorMessage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message: string | null) => {
        if (!message) return
        this.notification.create('error', 'Error', message)
        setTimeout(() => this.errorState.clear(), 5000)
      })
  }

  setMfaListenerSetting() {
    this.isMfaRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mfaRequired => {
        this.authState.patchState({
          mfaRequired
        })
      })
  }

  languageListenerSetting() {
    this.language$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string | null) => {
        if (data) {
          moment.locale(data)
          registerLocaleData(data)
          this.translate.use(data)
          return
        }
        this.preferenceState.setLanguage(
          // @ts-ignore
          navigator.language !== 'en' || navigator.language !== 'es'
            ? 'en'
            : navigator.language
        )
      })
  }

  setNotificationPermissionSetting() {
    combineLatest(this.userProfile$, this.notification$)
      .pipe(delay(2000), takeUntilDestroyed(this.destroyRef), take(2))
      .subscribe(data => {
        let [user, notification] = [data[0], data[1]]
        if (!user) return
        if (typeof notification === 'object') {
          this.modal.confirm({
            nzTitle: 'Do you want to receive notification for alerts?',
            nzOnOk: () => {
              this.preferenceState.setNotification(true)
              this.setSoundNotificationsModalSetting()
            },
            nzOnCancel: () => {
              this.preferenceState.setNotification(false)
            }
          })
        }
      })

    combineLatest(this.userProfile$, this.notification$)
      .pipe(delay(2000), takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        let [user, notification] = [data[0], data[1]]
        if (!user) return
        if (typeof notification === 'boolean' && notification) {
          this.notificationState.requestPermission()
        }
      })
  }

  setSoundNotificationsModalSetting() {
    this.modal.confirm({
      nzTitle: 'Do you want to receive sound notifications for alerts?',
      nzOnOk: () => {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(stream => {
            this.preferenceState.setPreferenceSoundNotifications(true)
          })
          .catch(error =>
            this.preferenceState.setPreferenceSoundNotifications(false)
          )
      },
      nzOnCancel: () => {
        this.preferenceState.setPreferenceSoundNotifications(false)
      }
    })
  }

  handlerCollapseMenuEmitter($event: boolean): void {
    this.preferenceState.setPreferenceIsCollapseMenu($event)
  }

  setRouterConfigLogic() {
    if (this.isMobile) return
    // this.router.resetConfig([...MobileRoutes])
  }

  public screenSaverClick(event: Event): void {
    if (this.isMobile) {
      event.stopPropagation()
    }
  }

  public hidePccLoginSettingsTabletError(): void {
    this.authState.pccLoginSettingsTabletErrorShow(null)
  }

  public pccLoginWasNotPerformedButClosed(): void {
    this.authState.setPccLoginWasNotPerformedButClosed(false)
    if (this.authState.accessToken()) {
      this.authState.handlePccLoginWindowClosing()
    }
  }

  public setEmrLoginSettingsTabletErrorInvitationTimeoutShown(): void {
    this.authState.setEmrLoginSettingsTabletErrorInvitationTimeoutShown(null)
  }

  public closeLoginFailedError(): void {
    this.authState.setLoginFailedError(null)
  }

  public notifySupportAboutLoginFailure(): void {
    const loginFailedError = this.loginFailedError()
    if (loginFailedError) {
      this.logrocketPFFacade.notifySupportAboutLoginFailure(
        loginFailedError.errorCode,
        loginFailedError.error
      )
    }
    this.authState.setLoginFailedError(null)
  }

  public pccLoginWasPerformedWithCNAUserClose(): void {
    this.preferenceState.setPccLoginWasPerformedWithCNAUser(false)
  }

  public setNonDegreeUserSelectedCNA(): void {
    this.preferenceState.setNonDegreeUserSelectedCNA(false)
  }

  public handlerLoginWithPcc(): void {
    this.authState.setPccLoginWasNotPerformedButClosed(false)
    this.authState.setIsPccReturning(true)
    this.authState.loginWithPcc(
      !this.authState.accessToken() ? 'login-tablet' : 'tablet'
    )
  }

  public declineDisclaimerLogout(): void {
    this.authState.logout()
    this.userState.setShouldWatchDisclaimer(false)
  }

  public acceptDisclaimer(): void {
    this.userState.acceptSignDisclaimer()
  }

  public setUserDegree(isCNA: boolean, shouldLogin: boolean = true): void {
    const user = this.userWithNoDegree()
    if (user) {
      this.userState.updateUserDegree(user.id, isCNA, shouldLogin)
    }
  }

  public setDegreeCNA(): void {
    this.setUserDegree(true, this.isMobile)
    if (this.isMobile) {
      return
    }
    if (this.router.url.includes('/login')) {
      this.authState.reset()
    } else {
      this.authState.logout()
    }
    this.preferenceState.setNonDegreeUserSelectedCNA(true)
  }

  public performUserLogout(): void {
    this.authState.logout(true)
  }

  public hideModal(): void {
    this.preferenceState.setSessionExpire(false)
  }

  public handlerScreenSaverSubmitEmitter(): void {
    this.preferenceState.setScreenSaver(false)
    this.batteryService.hideChargingOrPoweringScreen()
    this.batteryService.closeBatteryLowScreenPopup()
  }

  public handlerCloseClockUpdateModal($event: boolean): void {
    this.preferenceState.setIncorrectTimeSystem($event)
  }

  public hideChargingPoweringScreen(): void {
    this.batteryService.hideChargingOrPoweringScreen()
  }

  public closeBatteryLowScreenPopup(): void {
    this.batteryService.closeBatteryLowScreenPopup()
  }

  public selectNetwork(): void {
    MeasurementServicePlugin.openWiFiSettings()
    this.logrocketPFFacade.logrocketTrack(
      LogrocketTrackKeys.OpenWiFiNetworksSettings,
      { date: new Date().toISOString() }
    )
  }

  public restartSystemInTenMinutes(): void {
    this.capacitorPluginsFacade.restartSystemInTenMinutes()
  }

  public restartSystemNow(): void {
    this.capacitorPluginsFacade.restartSystemNow()
  }

  private pccBroadcastChannelListener() {
    const bc = new BroadcastChannel(PccWindowCommands.PccAiomedChannel)
    bc.postMessage(PccWindowCommands.PccAiomedChannelLoggedIn)
  }

  private controlDeviceSleepReturning(): void {
    if (!this.isMobile) {
      return
    }

    let timeHidden = new Date()
    let timeVisible = new Date()
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        timeHidden = new Date()
      } else {
        timeVisible = new Date()
      }
      if (
        !this.authState.isAuthenticated() ||
        !this.authState.refreshAccessToken()
      ) {
        return
      }

      const differenceInMinutes = Math.abs(
        moment(timeVisible).diff(moment(timeHidden), 'minutes')
      )
      const IDLE_TIME_MINUTES = 2
      if (
        document.location.href?.includes(this.bootstrapConfigService.loginUrl())
      ) {
        return
      }
      if (
        differenceInMinutes >= IDLE_TIME_MINUTES &&
        !document.hidden &&
        this.isOnline()
      ) {
        this.authState.handleSleepModeReturning(differenceInMinutes)
      }
    })
  }

  private monitorUserWindowTabFocusing(): void {
    document.addEventListener('visibilitychange', () => {
      const isVisible = !document.hidden
      this.preferenceState.setIsCurrentWindowTabInFocus(isVisible)

      if (!isVisible) {
        this.preferenceState.setTriggeredFocusedOutTime()
      }
    })
  }

  private handleNativeDeviceVirtualKeyboardExistence(): void {
    if (navigator && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
      this.preferenceState.setVirtualKeyboardPresence()
    }

    if (!('virtualKeyboard' in navigator)) {
      return
    }

    fromEvent((navigator as any).virtualKeyboard, 'geometrychange')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        // VirtualKeyboardGeometryChange
        this.preferenceState.setVirtualKeyboardPresence()
      })
  }

  private monitorScreenSaverChanges(): void {
    this.screenSaver$
      .pipe(takeUntilDestroyed())
      .subscribe(isScreenSaverShown => {
        MeasurementServicePlugin.setIsScreenSaverShown({ isScreenSaverShown })
      })
  }

  private handleFirstNavigationLoader(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ev => {
        if (ev instanceof NavigationStart) {
          // this.isInitialLoading.set(true)
        } else if (
          ev instanceof NavigationEnd ||
          ev instanceof NavigationError ||
          ev instanceof NavigationCancel ||
          ev instanceof NavigationSkipped ||
          ev instanceof ActivationEnd
        ) {
          this.isInitialLoading.set(false)
          this.isFirstNavigationPerformed.set(true)
        }
      })
  }

  private monitorAuthorizedStatus(): void {
    this.capacitorPluginsFacade.monitorAuthorizedStatus()
  }

  private listenToDeviceAboutToRestart(): void {
    this.capacitorPluginsFacade.listenToDeviceAboutToRestart()
  }

  private notifyNativePartAboutBeingAlive(): void {
    this.capacitorPluginsFacade.notifyNativePartAboutBeingAlive()
  }

  @HostListener('window:storage', ['$event'])
  public storageHandler(event: StorageEvent) {
    this.zoomVideoState.handleVideoInSessionChanges(event)
  }
}
